// Kontext osobního nastavení (téma, lišta, oblíbené): okamžitá změna v UI, záloha v localStorage,
// uložení na server přes PUT /ja/nastaveni s odkladem (debounce). Zdroj pravdy je server: po jeho
// úspěšné odpovědi (GET i PUT) se lokální kopie přepíše — pokud mezitím nepřibyla novější změna.
// Když endpoint chybí (404), pracuje se jen s localStorage a na server se dál neposílá.
import { useQuery } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  KLIC_NASTAVENI,
  NASTAVENI_VYCHOZI,
  nacistNastaveni,
  precistNastaveniZUloziste,
  ulozitNastaveni,
  zapsatNastaveniDoUloziste,
  type NastaveniUzivatele,
} from '../api/nastaveni';
import { ChybaApi } from '../api/klient';

/** Odklad uložení na server po poslední změně (tahání šířky lišty generuje desítky změn). */
export const ODKLAD_ULOZENI_MS = 600;

export interface NastaveniKontext {
  nastaveni: NastaveniUzivatele;
  /** Změní část nastavení; UI se překreslí hned, server dostane změnu s odkladem. */
  upravit: (zmena: Partial<NastaveniUzivatele>) => void;
  /** Přepne oblíbenou sekci ★ (Rychlé přístupy). */
  prepnoutOblibenou: (klic: string) => void;
  /** Otevře/zavře skupinu v liště. */
  prepnoutSkupinu: (klic: string) => void;
  /** Server nastavení zatím neumí (404) — platí jen lokální kopie. */
  serverChybi: boolean;
  /** Nastavení už přišlo ze serveru (nebo server chybí) — lokální záloha může být z jiného zařízení stará. */
  nacteno: boolean;
}

const Kontext = createContext<NastaveniKontext | null>(null);

interface Props {
  /** Id přihlášeného — klíč localStorage a `zamestnanec_id` v PUT. */
  zamestnanecId: string;
  children: ReactNode;
}

export function PoskytovatelNastaveni({ zamestnanecId, children }: Props) {
  const [nastaveni, setNastaveni] = useState<NastaveniUzivatele>(
    () => precistNastaveniZUloziste(zamestnanecId) ?? { ...NASTAVENI_VYCHOZI },
  );
  const [serverChybi, setServerChybi] = useState(false);
  // Číslo poslední lokální změny — odpověď serveru se použije jen, když od odeslání nic nepřibylo.
  const verze = useRef(0);
  const casovac = useRef<number | null>(null);
  const cekajici = useRef<NastaveniUzivatele | null>(null);

  const dotaz = useQuery({
    queryKey: [...KLIC_NASTAVENI, zamestnanecId],
    queryFn: ({ signal }) => nacistNastaveni(signal),
    retry: false,
    staleTime: Infinity,
  });

  // Odpověď GET: server má přednost před zálohou (jen když uživatel mezitím nic nezměnil).
  useEffect(() => {
    if (!dotaz.isSuccess) return;
    if (dotaz.data === null) {
      setServerChybi(true);
      return;
    }
    if (verze.current === 0) {
      setNastaveni(dotaz.data);
      zapsatNastaveniDoUloziste(zamestnanecId, dotaz.data);
    }
  }, [dotaz.isSuccess, dotaz.data, zamestnanecId]);

  const odeslat = useCallback(
    async (hodnota: NastaveniUzivatele, verzeOdeslani: number) => {
      try {
        const ulozene = await ulozitNastaveni(zamestnanecId, hodnota);
        if (verze.current === verzeOdeslani) {
          setNastaveni(ulozene);
          zapsatNastaveniDoUloziste(zamestnanecId, ulozene);
        }
      } catch (chyba) {
        if (chyba instanceof ChybaApi && chyba.status === 404) {
          setServerChybi(true);
          return;
        }
        // Nastavení je jen pohodlí — lokální kopie zůstává, příští změna to zkusí znovu.
        console.warn('Uložení nastavení na server se nepovedlo.', chyba);
      }
    },
    [zamestnanecId],
  );

  const naplanovatUlozeni = useCallback(
    (hodnota: NastaveniUzivatele) => {
      if (serverChybi) return;
      cekajici.current = hodnota;
      if (casovac.current !== null) window.clearTimeout(casovac.current);
      const verzeOdeslani = verze.current;
      casovac.current = window.setTimeout(() => {
        casovac.current = null;
        const k = cekajici.current;
        cekajici.current = null;
        if (k) void odeslat(k, verzeOdeslani);
      }, ODKLAD_ULOZENI_MS);
    },
    [odeslat, serverChybi],
  );

  // Aktuální hodnota i pro volání mimo render (bez vedlejších účinků v updateru setState).
  const aktualni = useRef(nastaveni);
  aktualni.current = nastaveni;

  const upravit = useCallback(
    (zmena: Partial<NastaveniUzivatele>) => {
      verze.current += 1;
      const nove = { ...aktualni.current, ...zmena };
      aktualni.current = nove;
      setNastaveni(nove);
      zapsatNastaveniDoUloziste(zamestnanecId, nove);
      naplanovatUlozeni(nove);
    },
    [naplanovatUlozeni, zamestnanecId],
  );

  const prepnoutOblibenou = useCallback(
    (klic: string) => {
      const ma = nastaveni.quick_links.includes(klic);
      upravit({
        quick_links: ma
          ? nastaveni.quick_links.filter((k) => k !== klic)
          : [...nastaveni.quick_links, klic],
      });
    },
    [nastaveni.quick_links, upravit],
  );

  const prepnoutSkupinu = useCallback(
    (klic: string) => {
      const zavrena = nastaveni.rail_groups_closed.includes(klic);
      upravit({
        rail_groups_closed: zavrena
          ? nastaveni.rail_groups_closed.filter((k) => k !== klic)
          : [...nastaveni.rail_groups_closed, klic],
      });
    },
    [nastaveni.rail_groups_closed, upravit],
  );

  // Téma na <html data-theme> — tokeny.css podle něj přepne barvy (volba uživatele vítězí nad systémem).
  useEffect(() => {
    document.documentElement.dataset.theme = nastaveni.theme;
    return () => {
      delete document.documentElement.dataset.theme;
    };
  }, [nastaveni.theme]);

  // Opuštění stránky (zavření záložky, F5, odhlášení) dřív, než doběhne odklad: poslat hned,
  // s keepalive, aby požadavek přežil. Bez čekání na odpověď — příště se stejně načte GET.
  useEffect(() => {
    const odeslatHned = () => {
      const k = cekajici.current;
      if (!k || serverChybi) return;
      cekajici.current = null;
      if (casovac.current !== null) {
        window.clearTimeout(casovac.current);
        casovac.current = null;
      }
      void ulozitNastaveni(zamestnanecId, k, { keepalive: true }).catch(() => undefined);
    };
    window.addEventListener('pagehide', odeslatHned);
    return () => {
      window.removeEventListener('pagehide', odeslatHned);
      odeslatHned();
    };
  }, [serverChybi, zamestnanecId]);

  const nacteno = dotaz.isSuccess || dotaz.isError || serverChybi;
  const hodnota = useMemo<NastaveniKontext>(
    () => ({ nastaveni, upravit, prepnoutOblibenou, prepnoutSkupinu, serverChybi, nacteno }),
    [nastaveni, upravit, prepnoutOblibenou, prepnoutSkupinu, serverChybi, nacteno],
  );

  return <Kontext.Provider value={hodnota}>{children}</Kontext.Provider>;
}

export function useNastaveni(): NastaveniKontext {
  const kontext = useContext(Kontext);
  if (!kontext) {
    throw new Error('useNastaveni je nutné volat uvnitř PoskytovatelNastaveni.');
  }
  return kontext;
}

/** Jako `useNastaveni`, ale mimo `PoskytovatelNastaveni` (samostatně vykreslená obrazovka, testy) vrátí null. */
export function useNastaveniVolitelne(): NastaveniKontext | null {
  return useContext(Kontext);
}
